import { Injectable } from '@angular/core';
import { HttpMethodType } from '@app/app.constant';
import { HttpCallService } from '@core/services/http-call.service';
import { EvideInputContentForm, IEvideInputContentFormHttp } from 'evide/models/evide-input-content';
import { ISaveWebformRecipientsRequest, IWebformRecipientsInfo } from '@app/models/forms/webform-recipient.model';
import { Observable, map } from 'rxjs';

export interface IPublishedFormVariantOption {
  id: number;
  variantGuid: string;
  title: string;
  description: string;
  sortOrder: number;
  isDefault: boolean;
}

export interface IPublishedFormVariants {
  formGuid: string;
  variantLabel: string | null;
  variants: IPublishedFormVariantOption[];
}

const HttpServiceMethods = {
  GetMyEvideInputContentForms: 'EvideInputContentForm/GetMyEvideInputContentForms',
  GetMyEvideInputContentFormsByFormType: 'EvideInputContentForm/GetMyEvideInputContentFormsByFormType',
  GetEvideFormByFormId: 'EvideInputContentForm/GetEvideFormByFormId',
  GetEvideFormByGuid: 'EvideInputContentForm/GetEvideFormByGuid',
  GetFormVariantLabelByGuid: 'EvideInputContentForm/GetFormVariantLabelByGuid',
  GetPublishedFormVariantsByFormType: 'EvideInputContentForm/GetPublishedFormVariantsByFormType',
  GetEvideInputContentFormsByProjectId: 'EvideInputContentForm/GetEvideInputContentFormsByProjectId', // Admin
  PublishEvideInputContentForm: 'EvideInputContentForm/PublishEvideInputContentForm',
  DeleteEvideInputContentFormById: 'EvideInputContentForm/DeleteEvideInputContentFormById',
  ArchiveEvideInputContentFormById: 'EvideInputContentForm/ArchiveEvideInputContentFormById',
  ExecuteRule: 'EvideInputContentForm/ExecuteRule',
  GetExecuteRuleParameterNamesByInputContentGuids: 'EvideInputContentForm/GetExecuteRuleParameterNamesByInputContentGuids',
  // Both authorise under EvideInputContentForm.PublishEvideInputContentForm (ActionName ride-along on the API).
  GetWebformRecipients: 'EvideInputContentForm/GetWebformRecipients',
  SaveWebformRecipients: 'EvideInputContentForm/SaveWebformRecipients'
};

@Injectable({
  providedIn: 'root'
})
export class EvideInputContentFormService {

  constructor(private httpService: HttpCallService) { }

  public getMyEvideInputContentForms(): Observable<EvideInputContentForm[]> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetMyEvideInputContentForms).pipe(
      map((items: IEvideInputContentFormHttp[]) =>
        items.map((item) => EvideInputContentForm.fromJson(item))
      )
    );
  }

  public getMyEvideInputContentFormsByFormType(formType: number): Observable<EvideInputContentForm[]> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetMyEvideInputContentFormsByFormType + `/${formType}`).pipe(
      map((items: IEvideInputContentFormHttp[]) =>
        items.map((item) => EvideInputContentForm.fromJson(item))
      )
    );
  }

  public getEvideFormByFormId(formId: number): Observable<EvideInputContentForm> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetEvideFormByFormId + `/${formId}`).pipe(
      map((item: IEvideInputContentFormHttp) => EvideInputContentForm.fromJson(item))
    );
  }

  // Cheap lookup for the fill-side variant selector. Returns the admin-defined label
  // ("Programme", "Language", etc.) or null when none is configured — caller falls back
  // to the default "Form variant" wording.
  public getFormVariantLabelByGuid(formGuid: string): Observable<string | null> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetFormVariantLabelByGuid + `/${formGuid}`).pipe(
      map((res: { variantLabel: string | null } | null) => res?.variantLabel ?? null)
    );
  }

  // Admin variant-pin pickers (program/activity settings): the current project's published
  // form of the given type with its variant list. Empty variants = nothing to pin, hide picker.
  public getPublishedFormVariantsByFormType(formType: number): Observable<IPublishedFormVariants> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetPublishedFormVariantsByFormType + `/${formType}`).pipe(
      map((res: IPublishedFormVariants | null) => res ?? { formGuid: '', variantLabel: null, variants: [] })
    );
  }


  public getEvideInputContentFormsByProjectId(projectId: number, evideFormTypeId: number): Observable<EvideInputContentForm[]> {
    return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetEvideInputContentFormsByProjectId + `/${projectId}/${evideFormTypeId}`).pipe(
      map((items: IEvideInputContentFormHttp[]) =>
        items.map((item) => EvideInputContentForm.fromJson(item))
      )
    );
  }

  public publish(id: number) {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.PublishEvideInputContentForm + `/${id}`);
  }

  public archive(id: number) {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.ArchiveEvideInputContentFormById + `/${id}`);
  }

  public delete(formId: number) {
    return this.httpService.callService(HttpMethodType.POST, `${HttpServiceMethods.DeleteEvideInputContentFormById}/${formId}`);
  }

  // Webform submission-alert recipients: selectable users (active, with access to the FORM's
  // project) plus the current selection. Web forms only - the API rejects other form types.
  public getWebformRecipients(formId: number): Observable<IWebformRecipientsInfo> {
    return this.httpService.callService(HttpMethodType.GET, `${HttpServiceMethods.GetWebformRecipients}/${formId}`);
  }

  public saveWebformRecipients(request: ISaveWebformRecipientsRequest): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.SaveWebformRecipients, request);
  }
}
